import React, { FC, useEffect, useRef } from 'react';
import { replaceMessageStringVariables } from '@shared/utils/replaceMessageStringVariables';
import DescriptionField from '@src/components/DescriptionField';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { AiSuggestionButton } from '@src/components/AiSuggestionButton';
import { useAppDispatch } from '@src/redux';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { useGenerateOpeningAutomatedResponseMutation } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import SectionTitle from '@src/components/SectionTitle';
import { AUTOMATED_RESPONSE_MAX_LENGTH } from '../../constants/textLength';
import { AutomatedResponseProps } from './props';
import './styles.scss';

export const AutomatedResponse: FC<AutomatedResponseProps> = ({
  automatedResponse,
  onFieldChange,
  openingId,
  company,
  openingTitle,
}) => {
  const dispatch = useAppDispatch();
  const abortControllerRef = useRef(new AbortController());

  const shouldEnableAiButtons = !!company?.hasPublishedProfile;

  const onAutomatedResponseChange = (newAutomatedResponse: string) => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    onFieldChange({
      automatedResponse: newAutomatedResponse.trim().length > 0 ? newAutomatedResponse : null,
    });
  };

  const [generateOpeningAutomatedResponse, { loading: isAiSuggestionLoading }] =
    useGenerateOpeningAutomatedResponseMutation({
      context: {
        fetchOptions: {
          signal: abortControllerRef.current.signal,
        },
      },
      onCompleted: ({ generateOpeningAutomatedResponse }) => {
        if (generateOpeningAutomatedResponse) {
          onAutomatedResponseChange(generateOpeningAutomatedResponse);
        } else {
          dispatch(
            setErrorFlashMessage('Generating the automated response is failing at the moment. Please try again later.'),
          );
        }
      },
      onError: () => {
        dispatch(
          setErrorFlashMessage('Generating the automated response is failing at the moment. Please try again later.'),
        );
      },
      variables: {
        openingId: openingId,
      },
    });

  useEffect(() => {
    if (shouldEnableAiButtons && !automatedResponse && !isAiSuggestionLoading) {
      generateOpeningAutomatedResponse();
    }
  }, []);

  return (
    <div className="automated-response">
      <div className="automated-response__field-column">
        <SectionTitle size="M" title="Automated response*" />
        <DescriptionField
          defaultValue={automatedResponse}
          inputClassName="automated-response__field-wrapper"
          maxLength={AUTOMATED_RESPONSE_MAX_LENGTH}
          onBlur={e =>
            trackEvent({
              context: MetricContext.CREATE_OPENING,
              interaction: MetricAction.SET_AUTOMATED_RESPONSE,
              payload: {
                'Automated Response': e.target.value,
                'Opening Id': openingId,
              },
            })
          }
          onChange={onAutomatedResponseChange}
          textFieldWrapperClassName="automated-response__field"
        />
        {shouldEnableAiButtons && (
          <AiSuggestionButton
            className="automated-response__ai-button"
            isLoading={isAiSuggestionLoading}
            onRegenerateClick={() => generateOpeningAutomatedResponse()}
          />
        )}
      </div>
      <div className="automated-response__info-column">
        <div className="automated-response__info-text">
          A friendly follow-up message to the applicant when they have answered to all of your questions.
          <br />
          <br />
          You can type in :name: to replace it with the applicant's name and :title: to replace it with the opening
          title.
        </div>

        {automatedResponse && (
          <div className="automated-response__info-preview">
            {replaceMessageStringVariables(automatedResponse, {
              name: 'James',
              title: openingTitle || '',
            })}
          </div>
        )}
      </div>
    </div>
  );
};
